import {
  Box,
  Button,
  Grid,
  Stack,
  Typography,
  Alert,
  Card,
  CardContent,
  CardHeader,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Checkbox,
} from '@mui/material';
import CustomersPermissions from './CustomersPermissions';
import CustomSnackbar from '../../components/CustomSnackbar';
import AlertDialogSlide from '../../components/Dialog/AlertDialogSlide';
import { ExpandMore } from '@mui/icons-material';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import MDBox from '../../components/MDBox';
import MDTypography from '../../components/MDTypography';
import DashboardNavbar from '../../examples/Navbars/DashboardNavbar';
import DashboardLayout from '../LayoutContainers/DashboardLayout';
import { PermissionBuildingCard } from '../../components/Permissions/PermissionBuildingCard';
import {
  fetchUsersDetails,
  fetchUserBuildingsDetails,
  updateUserPermissions,
  removeUserPermissions,
} from '../../stores/sensor/sensorStore';
import { getRoleDisplay } from '../../types/roles';
import ApartmentIcon from '@mui/icons-material/Apartment';

const diffCustomers = (originalCustomersIds, currentCustomersIds) => {
  const orig = new Set(originalCustomersIds.map(String));
  const curr = new Set(currentCustomersIds.map(String));

  const removed = [...orig].filter(id => !curr.has(id));
  const added = [...curr].filter(id => !orig.has(id));

  return {
    removedCount: removed.length,
    addedCount: added.length,
    removed, // ids removed
    added, // ids added
  };
};

const PermissionForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Get user ID from URL params
  const { userId } = useParams();

  // Redux selectors
  const { users } = useSelector(state => state.users);
  // if (users.length === 0) return null;
  const { buildings_details: buildings, loading } = useSelector(state => state.sensors);

  const selectedUser = useMemo(() => {
    if (!userId || !users || users.length === 0) return null;
    return users.find(user => user.id.toString() === userId.toString());
  }, [userId, users]);

  const [assignedCustomersInfo, setAssignedCustomersInfo] = useState([]);
  const [notAssignedCustomersInfo, setNotAssignedCustomersInfo] = useState([]);
  // Updated state structure - now building-specific
  const [buildingPermissions, setBuildingPermissions] = useState([]);
  // buildingPermissions structure: [{ building_id: 1, floor_and_area_ids: [1,2] }, { building_id: 2, floor_and_area_ids: null }]
  const [removePermissionsModalOpen, setRemovePermissionsModalOpen] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    if (!Array.isArray(users) || users.length === 0) return;
    if (!selectedUser || selectedUser.role !== 'd') return;

    const assignedCustomersIds = selectedUser.cust_ids ?? [];
    const availableCustomers = users.filter(u => u.role === 'c');
    const assignedSet = new Set(assignedCustomersIds.map(Number));

    setAssignedCustomersInfo(availableCustomers.filter(u => assignedSet.has(Number(u.id))));
    setNotAssignedCustomersInfo(availableCustomers.filter(u => !assignedSet.has(Number(u.id))));
  }, [users, selectedUser]);

  // Helper function to get user's current area permissions (updated for new structure)
  const getUserAreaPermissions = user => {
    if (!user || !user.building_permissions) return [];

    return user.building_permissions.map(perm => ({
      building_id: perm.building_id,
      // Convert string area IDs to numbers if they exist
      floor_and_area_ids: perm.floor_and_area_ids ? perm.floor_and_area_ids.map(id => Number(id)) : [],
    }));
  };

  const getDistribuotorCustomers = user => {
    if (!user || !user.cust_ids) return [];

    return user.cust_ids;
  };

  // Find the selected user

  // Get assigned building IDs
  const assignedBuildingIds = useMemo(() => {
    return buildingPermissions.map(perm => perm.building_id);
  }, [buildingPermissions]);

  // Get floors for a specific building, each with its areas list
  const getBuildingFloorsAndAreas = buildingId => {
    const building = buildings?.find(b => b.id === buildingId);
    if (!building) return [];

    const floors = Array.isArray(building.floors) ? building.floors : Object.values(building.floors || {});
    const areas = Array.isArray(building.areas) ? building.areas : Object.values(building.areas || {});
    const areasByFloor = new Map();

    areas.forEach(area => {
      const key = area?.floor;
      if (!areasByFloor.has(key)) areasByFloor.set(key, []);
      areasByFloor.get(key).push({
        ...area,
        displayName: `${area.name} (Floor ${area.floor})`,
      });
    });

    return floors.map(floorObj => ({
      ...floorObj,
      list: areasByFloor.get(floorObj.floor) || [],
    }));
  };

  const { assignedBuildings, availableBuildings } = useMemo(() => {
    if (!buildings || !assignedBuildingIds) {
      return { assignedBuildings: [], availableBuildings: [] };
    }

    const assigned = [];
    const available = [];

    buildings.forEach(building => {
      if (assignedBuildingIds.includes(building.id)) {
        assigned.push(building);
      } else {
        available.push(building);
      }
    });

    return { assignedBuildings: assigned, availableBuildings: available };
  }, [buildings, assignedBuildingIds]);

  // Enhanced change detection for building-specific permissions
  const hasAnyChanges = useMemo(() => {
    if (!selectedUser) return false;

    // ===== buildings diff (unchanged logic) =====
    const originalPermissions = getUserAreaPermissions(selectedUser);

    // length mismatch means changes
    let buildingChanged = buildingPermissions.length !== originalPermissions.length;

    if (!buildingChanged) {
      // compare building entries and their floor_and_area_ids (order-insensitive)
      buildingChanged = buildingPermissions.some(current => {
        const original = originalPermissions.find(o => o.building_id === current.building_id);
        if (!original) return true;

        const curIds = (current.floor_and_area_ids ?? []).slice().sort();
        const origIds = (original.floor_and_area_ids ?? []).slice().sort();
        return JSON.stringify(curIds) !== JSON.stringify(origIds);
      });
    }

    // ===== customers diff (NEW) =====
    // Only relevant for distributors
    let customersChanged = false;
    if (selectedUser.role === 'd') {
      const originalCustomersIds = (selectedUser.cust_ids ?? []).map(String);
      const currentCustomersIds = assignedCustomersInfo.map(c => String(c.id));

      const { removedCount, addedCount } = diffCustomers(originalCustomersIds, currentCustomersIds);

      customersChanged = removedCount > 0 || addedCount > 0;
    }

    return buildingChanged || customersChanged;
  }, [selectedUser, buildingPermissions, assignedCustomersInfo]);

  // Get changes summary for building-specific permissions
  const getChangesSummary = () => {
    if (!selectedUser) return { hasChanges: false, summary: [] };

    const changes = [];
    const originalPermissions = getUserAreaPermissions(selectedUser);
    const originalCustomersIds = getDistribuotorCustomers(selectedUser);

    // Check for building additions/removals
    const originalBuildingIds = originalPermissions.map(p => p.building_id);
    const currentBuildingIds = buildingPermissions.map(p => p.building_id);

    const currentCustomersIds = assignedCustomersInfo.map(c => c.id);

    const { removedCount, addedCount, removed, added } = diffCustomers(originalCustomersIds, currentCustomersIds);

    const addedBuildings = currentBuildingIds.filter(id => !originalBuildingIds.includes(id));
    const removedBuildings = originalBuildingIds.filter(id => !currentBuildingIds.includes(id));

    if (addedBuildings.length > 0) {
      changes.push(`${addedBuildings.length} building(s) to assign`);
    }
    if (removedBuildings.length > 0) {
      changes.push(`${removedBuildings.length} building(s) to remove`);
    }
    if (addedCount > 0) {
      changes.push(`${addedCount} customer(s) to assign`);
    }
    if (removedCount > 0) {
      changes.push(`${removedCount} customer(s) to remove`);
    }

    // Check for area permission changes in existing buildings
    buildingPermissions.forEach(current => {
      const original = originalPermissions.find(orig => orig.building_id === current.building_id);

      if (original) {
        const currentAreaIds = current.floor_and_area_ids || [];
        const originalAreaIds = original.floor_and_area_ids || [];

        if (JSON.stringify([...currentAreaIds].sort()) !== JSON.stringify([...originalAreaIds].sort())) {
          const buildingName =
            buildings?.find(b => b.id === current.building_id)?.name || `Building ${current.building_id}`;
          changes.push(`Area restrictions changed for ${buildingName}`);
        }
      }
    });

    return {
      hasChanges: changes.length > 0,
      summary: changes,
    };
  };

  // Initialize state when user data loads
  useEffect(() => {
    if (selectedUser) {
      const userPermissions = getUserAreaPermissions(selectedUser);
      setBuildingPermissions(userPermissions);
    }
  }, [selectedUser]);

  // Load data on component mount
  useEffect(() => {
    dispatch(fetchUsersDetails());
    dispatch(fetchUserBuildingsDetails());
  }, [dispatch]);

  // Handle assigning a building (with no area restrictions by default)
  const handleAssignBuilding = buildingId => {
    const selectedBuilding = buildings.find(b => b.id === buildingId);

    const allFloorsAndAreasIdsByDefault = [];

    Object.values(selectedBuilding.areas).map(area => allFloorsAndAreasIdsByDefault.push(area.id));
    Object.values(selectedBuilding.floors).map(floorObj => allFloorsAndAreasIdsByDefault.push(floorObj.id));

    setBuildingPermissions(prev => [
      ...prev,
      { building_id: buildingId, floor_and_area_ids: allFloorsAndAreasIdsByDefault }, // null = full building access
    ]);
  };

  // Handle unassigning a building
  const handleUnassignBuilding = buildingId => {
    setBuildingPermissions(prev => prev.filter(perm => perm.building_id !== buildingId));
  };

  // Handle area toggle for a specific building
  const handleAreaToggle = (buildingId, areaId) => {
    setBuildingPermissions(prev =>
      prev.map(perm => {
        if (perm.building_id === buildingId && perm.floor_and_area_ids !== null) {
          const currentAreaIds = perm.floor_and_area_ids || [];
          const newAreaIds = currentAreaIds.includes(areaId)
            ? currentAreaIds.filter(id => id !== areaId)
            : [...currentAreaIds, areaId];

          return { ...perm, floor_and_area_ids: newAreaIds };
        }
        return perm;
      }),
    );
  };

  // Updated submit function to match new API signature
  const handleSubmitAllChanges = async () => {
    try {
      if (!hasAnyChanges) {
        setNotification({
          open: true,
          message: 'No changes to submit',
          severity: 'info',
        });
        return;
      }

      // Convert to the expected format for the new API
      const buildingPermissionsForAPI = buildingPermissions.length > 0 ? buildingPermissions : [];

      // Call the updated API with the new signature
      await dispatch(
        updateUserPermissions({
          userId: selectedUser.id,
          buildingPermissions: buildingPermissionsForAPI,
          customersIds: assignedCustomersInfo.map(c => c.id.toString()),
        }),
      ).unwrap();

      setNotification({
        open: true,
        message: 'Successfully updated user permissions!',
        severity: 'success',
      });

      // Refresh data
      dispatch(fetchUsersDetails());
      dispatch(fetchUserBuildingsDetails());
    } catch (error) {
      console.error('Submit changes failed:', error);
      setNotification({
        open: true,
        message: error.message || 'Failed to update permissions',
        severity: 'error',
      });
    }
  };

  // Reset all changes
  const handleResetAllChanges = () => {
    if (selectedUser) {
      const userPermissions = getUserAreaPermissions(selectedUser);
      setBuildingPermissions(userPermissions);
      if (selectedUser.role === 'd') {
        const custSet = new Set(selectedUser.cust_ids.map(String));
        const inCustIds = users.filter(u => custSet.has(String(u.id)));
        const notInCustIds = users.filter(u => !custSet.has(String(u.id)) && u.role === 'c');

        setAssignedCustomersInfo(inCustIds);
        setNotAssignedCustomersInfo(notInCustIds);
      }
    }
  };

  if (!selectedUser) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="h6" color="error">
            User not found
          </Typography>
          <Button variant="text" onClick={() => navigate('/manage-permissions/users')} sx={{ mt: 2 }}>
            ← Back to Permissions
          </Button>
        </Box>
      </DashboardLayout>
    );
  }

  const handleRemoveAllPermissions = async () => {
    try {
      await dispatch(removeUserPermissions(selectedUser.id)).unwrap();

      navigate('/manage-permissions/users');

      setNotification({
        open: true,
        message: 'All permissions removed successfully!',
        severity: 'success',
      });

      // Data will be refreshed automatically by the thunk
    } catch (error) {
      console.error('Remove permissions failed:', error);
      setNotification({
        open: true,
        message: error.message || 'Failed to remove permissions',
        severity: 'error',
      });
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar objectName={selectedUser.username} />

      {/* Header */}
      <MDBox
        my={1}
        px={2}
        py={2}
        variant="gradient"
        style={{
          background: '#3244E2',
        }}
        borderRadius="lg"
        coloredShadow="info">
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" marginLeft={2} spacing={2} alignItems="end">
            <MDTypography variant="h4" color="light">
              Manage Permissions: {selectedUser.username}
            </MDTypography>
          </Stack>
        </Stack>
      </MDBox>

      <Box
        sx={{
          margin: {
            xs: '0px 0px',
            md: '0px 10rem',
            lg: '0px 22rem',
            xl: '0px 30rem',
            xxl: '0px 42rem',
          },
        }}>
        <Grid container spacing={3}>
          {/* USER INFO SECTION */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="User Information" sx={{ color: 'white' }} />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="body2">
                      <strong>Username:</strong> {selectedUser.username}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2">
                      <strong>Role:</strong> {getRoleDisplay(selectedUser.role)}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2">
                      <strong>Current Buildings:</strong> {assignedBuildings.length}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* ASSIGNED BUILDINGS SECTION */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader
                title={`Assigned Buildings (${assignedBuildings.length})`}
                sx={{
                  bgcolor: 'success.main',
                  '& .MuiCardHeader-title': { color: '#ffffff' },
                }}
              />
              <CardContent
                sx={{
                  minHeight: '400px',
                  maxHeight: '600px',
                  overflow: 'auto',
                }}>
                {assignedBuildings.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No buildings assigned to this user
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {assignedBuildings.map(building => {
                      const buildingPerm = buildingPermissions.find(p => p.building_id === building.id);
                      const hasAreaRestrictions = buildingPerm?.floor_and_area_ids !== null;
                      const areaCount = hasAreaRestrictions
                        ? buildingPerm?.floor_and_area_ids?.length || 0
                        : 'Full Access';

                      return (
                        <Grid item xs={12} key={building.id}>
                          <Box
                            sx={{
                              cursor: 'pointer',
                              '&:hover': {
                                transform: 'scale(1.02)',
                                transition: 'transform 0.2s ease-in-out',
                              },
                              transition: 'transform 0.2s ease-in-out',
                              position: 'relative',
                            }}
                            onClick={() => handleUnassignBuilding(building.id)}>
                            <PermissionBuildingCard
                              id={building.id}
                              name={building.name}
                              country={building.country}
                              city={building.city}
                              street={building.street}
                              devices_count={building.devices_count}
                            />
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* AVAILABLE BUILDINGS SECTION */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader
                title={`Available Buildings (${availableBuildings.length})`}
                sx={{
                  bgcolor: 'primary.main',
                  '& .MuiCardHeader-title': { color: '#ffffff' },
                }}
              />
              <CardContent
                sx={{
                  minHeight: '400px',
                  maxHeight: '600px',
                  overflow: 'auto',
                }}>
                {availableBuildings.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      All buildings are already assigned to this user
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {availableBuildings.map(building => (
                      <Grid item xs={12} key={building.id}>
                        <Box
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              transform: 'scale(1.02)',
                              transition: 'transform 0.2s ease-in-out',
                            },
                            transition: 'transform 0.2s ease-in-out',
                          }}
                          onClick={() => handleAssignBuilding(building.id)}>
                          <PermissionBuildingCard
                            id={building.id}
                            name={building.name}
                            country={building.country}
                            city={building.city}
                            street={building.street}
                            devices_count={building.devices_count}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* FLOOR AND AREA RESTRICTIONS SECTION */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="Floor And Area Restrictions"
                sx={{
                  bgcolor: 'secondary.main',
                  '& .MuiCardHeader-title': { color: '#ffffff' },
                }}
              />
              <CardContent>
                <Typography variant="body2" sx={{ mb: 3 }}>
                  Configure area access restrictions for each assigned building. By default, users have full access to
                  all areas in their buildings.
                </Typography>

                {assignedBuildings.length === 0 ? (
                  <Box
                    sx={{
                      textAlign: 'center',
                      py: 4,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                    }}>
                    <Typography variant="body2" color="text.secondary">
                      Assign buildings first to configure area restrictions
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {assignedBuildings.map(building => {
                      const buildingPerm = buildingPermissions.find(p => p.building_id === building.id);
                      const hasAreaRestrictions = buildingPerm?.floor_and_area_ids !== null;
                      const availableFloors = getBuildingFloorsAndAreas(building.id);
                      const availableAreas = availableFloors.flatMap(floor => floor.list || []);
                      const selectedAreaIds = buildingPerm?.floor_and_area_ids || [];
                      const isFloorFullySelected = floor => {
                        const floorAreas = floor.list || [];
                        const floorAreaIds = floorAreas.map(area => area.id);
                        if (floorAreas.length > 0) {
                          return floorAreaIds.every(id => selectedAreaIds.includes(id));
                        }
                        return selectedAreaIds.includes(floor.id);
                      };
                      const isFloorSelected = floor => {
                        const floorAreas = floor.list || [];
                        const floorAreaIds = floorAreas.map(area => area.id);
                        if (floorAreas.length > 0) {
                          return floorAreaIds.some(id => selectedAreaIds.includes(id));
                        }
                        return selectedAreaIds.includes(floor.id);
                      };
                      const selectedFloorCount = availableFloors.filter(isFloorSelected).length;

                      return (
                        <Grid item xs={12} key={building.id}>
                          <Accordion sx={{ boxShadow: 'none', border: `1px solid #e3e3e3` }}>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.5,
                                }}>
                                <ApartmentIcon sx={{ color: '#344767', scale: 1.3 }} />
                                <Typography variant="h6">{building.name}</Typography>
                                <Chip
                                  label={
                                    hasAreaRestrictions
                                      ? `${selectedFloorCount}/${availableFloors.length} Floors Selected`
                                      : 'Full Access'
                                  }
                                  size="small"
                                  color={selectedFloorCount === availableFloors.length ? 'success' : 'warning'}
                                />
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                              {hasAreaRestrictions && (
                                <>
                                  {availableFloors.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                      No floors available in this building
                                    </Typography>
                                  ) : (
                                    <>
                                      <Box
                                        sx={{
                                          overflow: 'auto',
                                          border: '1px solid #e0e0e0',
                                          borderRadius: 1,
                                          bgcolor: 'grey.300',
                                        }}>
                                        {availableFloors.map(floor => {
                                          const floorAreas = floor.list || [];
                                          const floorAreaIds = floorAreas.map(area => area.id);
                                          const floorSelectionIds = [floor.id, ...floorAreaIds].filter(Boolean);
                                          const isFloorChecked = isFloorFullySelected(floor);
                                          const isFloorIndeterminate =
                                            !isFloorChecked &&
                                            floorSelectionIds.some(id => selectedAreaIds.includes(id));
                                          const selectedFloorAreaCount = floorAreaIds.filter(id =>
                                            selectedAreaIds.includes(id),
                                          ).length;

                                          return (
                                            <Accordion
                                              key={floor.id}
                                              sx={{
                                                boxShadow: 'none',
                                                '&.Mui-expanded': { margin: '1px 0' },
                                              }}>
                                              <AccordionSummary expandIcon={<ExpandMore />}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                  <Checkbox
                                                    checked={isFloorChecked}
                                                    indeterminate={isFloorIndeterminate}
                                                    onClick={event => event.stopPropagation()}
                                                    onChange={() => {
                                                      setBuildingPermissions(prev =>
                                                        prev.map(perm => {
                                                          if (perm.building_id !== building.id) return perm;
                                                          const currentIds = perm.floor_and_area_ids || [];
                                                          const nextIds = isFloorChecked
                                                            ? currentIds.filter(id => !floorSelectionIds.includes(id))
                                                            : Array.from(
                                                                new Set([...currentIds, ...floorSelectionIds]),
                                                              );
                                                          return { ...perm, floor_and_area_ids: nextIds };
                                                        }),
                                                      );
                                                    }}
                                                  />
                                                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                    Floor {floor.floor}
                                                  </Typography>
                                                  <Chip
                                                    label={`${selectedFloorAreaCount}/${floorAreaIds.length} Areas`}
                                                    size="small"
                                                    color={
                                                      selectedFloorAreaCount === floorAreaIds.length
                                                        ? 'success'
                                                        : 'warning'
                                                    }
                                                  />
                                                  <Typography variant="caption" color="text.secondary">
                                                    {floorAreas.length} areas
                                                  </Typography>
                                                </Box>
                                              </AccordionSummary>
                                              <AccordionDetails>
                                                {floorAreas.length === 0 ? (
                                                  <Typography variant="body2" color="text.secondary">
                                                    No areas are configured in this floor
                                                  </Typography>
                                                ) : (
                                                  <Grid container spacing={1}>
                                                    {floorAreas.map(area => (
                                                      <Grid item xs={12} sm={6} md={4} key={area.id}>
                                                        <Box
                                                          onClick={() => handleAreaToggle(building.id, area.id)}
                                                          sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            p: 1,
                                                            borderRadius: 1,
                                                            cursor: 'pointer',
                                                            backgroundColor: 'white',
                                                            border: '2px solid',
                                                            borderColor: selectedAreaIds.includes(area.id)
                                                              ? 'primary.main'
                                                              : 'grey.300',
                                                            '&:hover': {
                                                              borderColor: 'primary.main',
                                                            },
                                                            transition: 'all 0.2s ease-in-out',
                                                          }}>
                                                          <Box
                                                            sx={{
                                                              width: 16,
                                                              height: 16,
                                                              border: '2px solid',
                                                              borderColor: selectedAreaIds.includes(area.id)
                                                                ? 'primary.main'
                                                                : 'grey.400',
                                                              borderRadius: 0.5,
                                                              backgroundColor: selectedAreaIds.includes(area.id)
                                                                ? 'primary.main'
                                                                : 'transparent',
                                                              display: 'flex',
                                                              alignItems: 'center',
                                                              justifyContent: 'center',
                                                              mr: 1,
                                                              flexShrink: 0,
                                                            }}>
                                                            {selectedAreaIds.includes(area.id) && (
                                                              <Box
                                                                sx={{
                                                                  width: 8,
                                                                  height: 8,
                                                                  backgroundColor: 'white',
                                                                  borderRadius: 0.25,
                                                                }}
                                                              />
                                                            )}
                                                          </Box>
                                                          <Typography
                                                            variant="body2"
                                                            sx={{
                                                              fontSize: '0.875rem',
                                                              color: selectedAreaIds.includes(area.id)
                                                                ? 'primary.main'
                                                                : 'text.primary',
                                                              fontWeight: selectedAreaIds.includes(area.id)
                                                                ? 'medium'
                                                                : 'regular',
                                                            }}>
                                                            {area.displayName}
                                                          </Typography>
                                                        </Box>
                                                      </Grid>
                                                    ))}
                                                  </Grid>
                                                )}
                                              </AccordionDetails>
                                            </Accordion>
                                          );
                                        })}
                                      </Box>

                                      <Box
                                        sx={{
                                          mt: 2,
                                          display: 'flex',
                                          gap: 2,
                                          alignItems: 'center',
                                        }}>
                                        <Button
                                          size="small"
                                          onClick={() => {
                                            setBuildingPermissions(prev =>
                                              prev.map(perm =>
                                                perm.building_id === building.id
                                                  ? {
                                                      ...perm,
                                                      floor_and_area_ids: availableAreas.map(area => area.id),
                                                    }
                                                  : perm,
                                              ),
                                            );
                                          }}
                                          disabled={selectedFloorCount === availableFloors.length}>
                                          Select All
                                        </Button>
                                        <Button
                                          size="small"
                                          onClick={() => {
                                            setBuildingPermissions(prev =>
                                              prev.map(perm =>
                                                perm.building_id === building.id
                                                  ? { ...perm, floor_and_area_ids: [] }
                                                  : perm,
                                              ),
                                            );
                                          }}
                                          disabled={selectedAreaIds.length === 0}>
                                          Clear All
                                        </Button>
                                      </Box>
                                    </>
                                  )}
                                </>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* ASSIGN CUSTOMERS SECTION */}
          {selectedUser.role === 'd' && (
            <CustomersPermissions
              allUsers={users}
              selectedUser={selectedUser}
              assignedCustomersInfo={assignedCustomersInfo}
              setAssignedCustomersInfo={setAssignedCustomersInfo}
              notAssignedCustomersInfo={notAssignedCustomersInfo}
              setNotAssignedCustomersInfo={setNotAssignedCustomersInfo}
            />
          )}
          {/* SUBMIT CHANGES SECTION */}
          {(() => {
            const { hasChanges: hasAnyChangesToSubmit, summary } = getChangesSummary();

            return (
              hasAnyChangesToSubmit && (
                <Grid item xs={12}>
                  <Card>
                    <CardHeader
                      title="Unsaved Changes"
                      sx={{
                        bgcolor: 'warning.main',
                        '& .MuiCardHeader-title': { color: '#ffffff' },
                      }}
                    />
                    <CardContent>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        You have unsaved permission changes:
                      </Typography>
                      {summary.map((change, index) => (
                        <Typography key={index} variant="body2" sx={{ mb: 1, ml: 1 }}>
                          • {change}
                        </Typography>
                      ))}
                      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleSubmitAllChanges}
                          disabled={loading}
                          sx={{ minWidth: '150px' }}>
                          {loading ? 'Submitting...' : 'Submit All Changes'}
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={handleResetAllChanges}
                          sx={{ borderColor: 'blue', color: 'blue' }}
                          disabled={loading}>
                          {' '}
                          Reset All Changes
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )
            );
          })()}

          {/* DANGER ZONE SECTION */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="Danger Zone"
                sx={{
                  bgcolor: 'error.main',
                  '& .MuiCardHeader-title': { color: '#ffffff' },
                }}
              />
              <CardContent>
                <Typography variant="body1" gutterBottom>
                  Remove all building and area permissions for this user. This action cannot be undone.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  The user will lose access to all buildings and areas assigned to them.
                </Typography>
                <Button
                  variant="inherit"
                  color="error"
                  onClick={() => setRemovePermissionsModalOpen(true)}
                  disabled={loading || assignedBuildings.length === 0}
                  sx={{
                    borderColor: 'red',
                    color: 'red',
                    border: `1px solid red`,
                    '&:hover': { background: '#ff0000', color: '#fff' },
                  }}>
                  Remove All Permissions
                </Button>
                {assignedBuildings.length === 0 && (
                  <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                    User has no permissions to remove
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Back Button */}
          <Grid item xs={12}>
            <Button variant="text" onClick={() => navigate('/manage-permissions/users')} sx={{ mt: 2 }}>
              ← Back to Permissions
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Notification Snackbar */}
      <CustomSnackbar
        open={notification.open}
        duration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        vertical="top"
        horizontal="right"
        severity={notification.severity}
        message={notification.message}
      />

      {/* Remove All Permissions Confirmation Modal */}
      <AlertDialogSlide
        open={removePermissionsModalOpen}
        title="Confirm Remove All Permissions"
        content={`Are you sure you want to remove ALL permissions for user "${selectedUser?.username}"? This will remove access to all ${assignedBuildings.length} building(s) and their areas. This action cannot be undone.`}
        handleClose={() => setRemovePermissionsModalOpen(false)}
        handleOk={handleRemoveAllPermissions}
      />
    </DashboardLayout>
  );
};

export default PermissionForm;
